import type React from 'react';
import { useMemo } from 'react';
import { FormProvider, FieldProvider, useFieldContext } from './FormContext';
import { SelectField } from './SelectField';
import { ColorField } from './ColorField';
import { NumberField } from './NumberField';
import { RangeField } from './RangeField';
import type { FormField, FormValues } from './types';
import styles from './FormRenderer.module.css';

// BaseField component moved into FormRenderer
interface BaseFieldProps {
  children: React.ReactNode;
  className?: string;
}

const BaseField: React.FC<BaseFieldProps> = ({ 
  children, 
  className = '' 
}) => {
  return (
    <div className={`${styles.field} ${className}`}>
      <div className={styles.fieldContent}>
        <div className={styles.fieldInput}>
          {children}
        </div>
      </div>
    </div>
  );
};

const FieldInput: React.FC = () => {
  const { field } = useFieldContext();
  
  switch (field.type) {
    case 'select':
      return <SelectField />;
    case 'color':
      return <ColorField />;
    case 'number':
      return <NumberField />;
    case 'range':
      return <RangeField />;
    default:
      return null;
  }
};

const FieldComponent: React.FC<{ field: FormField }> = ({ field }) => {
  const colspan = field.colspan || 1;
  const colspanClass = styles[`colspan${colspan}`];
  
  return (
    <FieldProvider field={field}>
      <BaseField className={colspanClass}>
        <FieldInput />
      </BaseField>
    </FieldProvider>
  );
};

interface FormRendererProps {
  config: FormField[];
  values: FormValues;
  onChange: (values: FormValues) => void;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  config,
  values,
  onChange,
}) => {
  // Memoize the rendered fields to prevent unnecessary re-renders
  const renderedFields = useMemo(
    () => config.map((field) => <FieldComponent key={field.name} field={field} />),
    [config]
  );

  return (
    <FormProvider values={values} onChange={onChange}>
      <div className={styles.formGrid}>{renderedFields}</div>
    </FormProvider>
  );
};
